import { useMemo } from "react";
import { qrSvgDataUrl } from "@workspace/player-shell/signage";
import { t } from "@workspace/strings/web";
import { qrConfig } from "@/player/lib/widgets";

/**
 * Fullscreen QR code for config.url (menu, loyalty signup, wifi portal…).
 * Uses the shared encoder in `@workspace/player-shell/signage` — the same
 * modules the native shells and the printed device labels produce — rendered
 * as an inline SVG data URL so it stays vector-crisp at TV sizes.
 */
export function QrWidget({
  config,
}: {
  config: Record<string, unknown> | null;
}) {
  const cfg = qrConfig(config);
  const src = useMemo(() => (cfg ? qrSvgDataUrl(cfg.url) : null), [cfg?.url]);

  if (!cfg) {
    return (
      <div
        className="flex h-full w-full items-center justify-center bg-background text-muted-foreground"
        data-testid="widget-qr"
      >
        {t("player.widgets.qr.needsUrl")}
      </div>
    );
  }

  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-6 bg-background"
      data-testid="widget-qr"
    >
      {src ? (
        // White plate regardless of theme — a tinted quiet zone is the usual
        // reason a screen-displayed code fails to scan.
        <div className="rounded-2xl bg-white p-[2vmin] shadow-sm">
          <img
            src={src}
            alt=""
            className="block size-[52vmin]"
            data-testid="widget-qr-image"
          />
        </div>
      ) : (
        // Content too long to encode: the caption still tells staff what this
        // tile is, and the URL keeps it actionable.
        <div className="text-muted-foreground text-[2.5vw] break-all px-8 text-center">
          {cfg.url}
        </div>
      )}
      {cfg.caption && (
        <div className="text-foreground text-[3.5vw] font-medium text-center px-8">
          {cfg.caption}
        </div>
      )}
    </div>
  );
}
